<template>
  <div class="position-relative">
    <div class="focus-mode" :class="inPreviewReport && 'd-block'"></div>
    <page-header
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    >
      <action-button
        v-if="!editMode"
        id="btn-back"
        variant="outline-success mr-2"
        :class="isMobile && 'w-50'"
        type="button"
        i="fas fa-arrow-circle-left pr-2"
        @click="() => this.$router.push('/special')"
      >
        Regresar
      </action-button>
      <action-button
        v-if="(!readOnly && !editMode) || onCopyMode"
        id="btn-save"
        variant="success"
        :class="isMobile && 'w-50'"
        type="button"
        i="fa-solid fa-flag pr-2"
        @click="save"
      >
        Someter
      </action-button>
      <action-button
        v-if="readOnly && canEdit && !onCopyMode"
        id="btn-edit"
        variant="success"
        :class="isMobile && 'w-50'"
        type="button"
        i="fas fa-edit pr-2"
        @click="
          () => {
            this.editMode = true
          }
        "
      >
        Editar
      </action-button>
      <action-button
        v-if="editMode"
        id="btn-cancelar"
        variant="outline-danger mr-2"
        :class="isMobile && 'w-50'"
        type="button"
        i="fas fa-times-circle pr-2"
        @click="cancel"
      >
        Cancelar
      </action-button>
      <action-button
        v-if="editMode"
        id="btn-edit"
        variant="success"
        :class="isMobile && 'w-50'"
        type="button"
        i="fas fa-save pr-2"
        @click="save"
      >
        Guardar
      </action-button>
    </page-header>
    <validation-observer ref="specialReport" v-slot="{}" tag="form">
      <general-information
        v-model="model"
        :read-only="readOnly || onPreview"
        :copy-mode="onCopyMode"
      />
      <topic
        v-model="model"
        :read-only="topicReadOnly || onPreview"
        :is-mobile="isMobile"
      />
      <fields
        v-model="model"
        :error="isSubmitted && model.columns.length < 3"
        :read-only="readOnly"
        :on-preview="onPreview"
        :edit-mode="editMode"
        :copy-mode="onCopyMode"
        v-show="model.topic"
      />
      <preview-report
        v-if="!readOnly || onCopyMode"
        v-model="model"
        @preview="setReadOnly"
        :edit-mode="editMode"
        :copy-mode="onCopyMode"
        :class="inPreviewReport && 'position-relative focus-mode-preview'"
      />
      <div class="mb-5 mb-5">
        <section-title class="mt-3" content="Filtros" :has-line="true" />
        <filters
          :copy-mode="onCopyMode"
          :edit-mode="editMode"
          :read-only="readOnly || onPreview"
          v-model="model.filters"
          :data="model"
          :conditionals="conditionals"
          :is-mobile="isMobile"
          :error="!readOnly && isSubmitted && model.filters.length == 0"
        />
        <generate-script
          v-if="readOnly && canScriptViewer && !onCopyMode"
          v-model="model.scriptText"
          :is-mobile="isMobile"
        />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import PageHeader from 'PageHeader'
import GeneralInformation from './components/GeneralInformation'
import Topic from './components/Topic'
import Fields from './components/Fields'
import PreviewReport from './components/PreviewReport'
import Filters from './components/Filters'
import GenerateScript from './components/Script'
import { ValidationObserver } from 'vee-validate'
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js'
import SpecialService from '@/services/api/special.api'
import SectionTitle from 'SectionTitle'
import ActionButton from 'ActionButton'
import rbDataType from '@/utils/constants/rbDataType.js'

//constants
const SPECIAL_REPORT_MANAGEMENT_ROUTE_NAME = 'SpecialReportManager'

export default {
  name: 'SpecialReportsManager',
  components: {
    PageHeader,
    GeneralInformation,
    Topic,
    Fields,
    PreviewReport,
    Filters,
    GenerateScript,
    ValidationObserver,
    SectionTitle,
    ActionButton,
  },
  mixins: [alertMixin],
  data: () => ({
    model: {
      id: null,
      name: null,
      description: null,
      topic: null,
      columns: [],
      filters: [],
      locationFilters: [],
      listOfTopics: [],
      sharedWithMe: false,
      scriptText: '',
    },
    isSubmitted: false,
    results: [],
    inPreviewReport: false,
    editMode: false,
    conditionals: [],
  }),
  computed: {
    isMobile() {
      return this.$store.getters['$_app/getIsMobileDensity']
    },
    headerConfig() {
      return {
        title: 'Informes Especiales',
        icon: 'fa-solid fa-magnifying-glass-chart pr-0 font-22 magnifying-glass-icon-m ',
        routes: ['Inicio', 'Informes Especiales', 'Crear Informe'],
      }
    },
    readOnly() {
      return this.model?.id > 0 && !this.editMode && !this.onCopyMode
    },
    topicReadOnly() {
      return this.model?.id > 0
    },
    onPreview() {
      return this.inPreviewReport
    },
    reportId() {
      return this.$route.params.id || null
    },
    canScriptViewer() {
      return this.userHasPermissions('report.specialInform.showScript')
    },
    canEdit() {
      return !this.model.sharedWithMe
    },
    onCopyMode() {
      const { copy } = this.$route.query
      return typeof copy === 'boolean' ? copy : copy === 'true'
    },
  },
  mounted() {
    this.getConditionals()
    if (this.reportId) this.getReport(this.reportId)
  },
  methods: {
    async save() {
      this.isSubmitted = true
      const isValid = await this.$refs.specialReport.validate()

      if (
        !isValid ||
        this.model.columns.length < 3 ||
        this.model.filters.length === 0
      ) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        )
        return
      }
      const subTopics = this.model.columns.map((i) => i.subTopicId)
      const uniqueSubTopics = [...new Set(subTopics)]

      let payload = {
        id: this.onCopyMode ? null : this.model.id,
        Name: this.model.name,
        Description: this.model.description,
        TopicId: this.model.topic?.id
          ? this.model.topic.id
          : parseInt(this.model.topic),
        SelectedColumns: this.model.columns
          .filter((e) => !e['auto-check'])
          .map((e, idx) => ({
            id: e.id,
            subTopicId: e.subTopicId,
            tableId: e.tableId,
            order: idx,
          })),
        SelectedFilters: this.model.filters.map((e) => ({
          subTopicId: e.subTopicId,
          id: e.id,
          columnName: e.columnName,
          tableId: e.tableId,
          conditional: e.model.conditional,
          dataType: e.dataType,
          ...this.getValue(e),
        })),
        SelectedFilterByLocation: this.model.locationFilters,
        SubTopics: uniqueSubTopics,
      }

      const { data } = await SpecialService.saveReport(payload).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error')
      })

      if (data) {
        this.ShowToast(
          'Proceso Exitoso',
          this.editMode
            ? 'Informe especial editado exitosamente'
            : 'Informe especial creado exitosamente',
          'success'
        )
        if (this.editMode) {
          await this.getReport(data)
          this.editMode = false
          return
        }
        this.$router.push({
          name: SPECIAL_REPORT_MANAGEMENT_ROUTE_NAME,
          params: {
            id: data,
          },
        })
      }
    },
    async getReport(id) {
      const { data } = await SpecialService.getSpecialReport(id)
      if (data) {
        this.model.id = data.id
        this.model.name = data.name
        this.model.description = data.description
        this.model.columns = [...data.selectedColumns]
        this.model.filters = [...data.selectedFilters]
        this.model.topic = data.topic
        this.model.scriptText = data.scriptTemplate
        this.model.sharedWithMe = data.sharedWithMe

        if (this.onCopyMode) {
          this.model.filters = this.model.filters.map((e) => {
            let data = null
            if (e?.value || e?.model?.value) {
              const value = e.value ? e.value : e.model.value
              if (value) {
                const id = typeof value === 'string' ? value : value.id

                let result = null

                if (e.dataType == rbDataType.Select) {
                  if (e.isMultiSelect) {
                    const ids = e.value.split(',').map((e) => parseInt(e))
                    result = e.source
                      ?.filter((m) => ids.includes(m.id))
                      .map((e) => ({ ...e, selected: true }))
                  } else {
                    result = e.source?.find((m) => m.id == id)
                  }
                }

                data = e.dataType == rbDataType.Select ? result : value
              }
            }
            return {
              ...e,
              model: {
                conditional: this.conditionals[0],
                value: data,
              },
            }
          })
        }
      }
    },
    setReadOnly(value) {
      this.inPreviewReport = value
    },
    getValue(e) {
      const { dataType, isMultiSelect } = e
      if (dataType === rbDataType.Date && isMultiSelect) {
        if(Array.isArray(e.model.value) && e.model.value.length > 1) {
          return {
            isMultiSelect: true,
            value: new Date(e.model.value[0]).toLocaleDateString(),
            value2: new Date(e.model.value[1]).toLocaleDateString(),
          }
        }
      }

      let value;
      if(typeof e.model.value === 'object') {
        if(Array.isArray(e.model.value)) {
          value = e.model.value.map((i) => i.id).join(',');
        }
        else if(e.model.value.id) {
          value = e.model.value.id.toString();
        }
        else if(e.model.value instanceof Date) {
          value = new Date(e.model.value.toLocaleDateString("en-US"));
        }
        else {
          value = e.model.value;
        }
      }
      else{
        value = e.model.value;
      }

      return { value }
    },
    async cancel() {
      await this.getReport(this.reportId)
      this.editMode = false
    },
    async getConditionals() {
      const { data } = await SpecialService.getConditionals()
      this.conditionals = data
    },
  },
  beforeRouteUpdate(to, from, next) {
    this.getReport(to.params.id)
    next()
  },
}
</script>
<style lang="scss" scoped>
.focus-mode {
  display: none;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
  opacity: 0.6;
  background-color: black;
  z-index: 9999;
}

.focus-mode-preview {
  z-index: 99999;
}

@media only screen and (max-width: 600px) {
  ::v-deep .mobile-actions {
    display: flex !important;
    justify-content: space-between !important;
    margin-bottom: 1rem;
  }

  :v-deep .mobile-actions:first-child {
    width: 45% !important;
  }
}
</style>
